import React, { ReactNode, useEffect, useMemo, useState } from 'react';
import { useAppDispatch, useAppSelector } from 'app/store';
import {
  getLyricSheet,
  getLyricVersion,
  getSong,
} from 'app/store/SongList/selectors';
import { ContentContainerType, ApiErrorCodeTypes } from 'types/enums';
import { LyricsFormLoader } from 'app/components/Song/Lyrics/LyricsFormLoader';
import {
  downloadLyricsPdfEffect,
  getLyrics2ConnectionEffect,
} from 'app/store/SongList/effects';
import { useProvider } from 'app/components/Song/Lyrics/TextEditor/hooks';
import { Lyrics2Configuration } from 'types/lyrics.types';
import { TextEditor } from 'app/components/Song/Lyrics/TextEditor';
import { NotSupportedError } from 'app/components/Song/Lyrics/NotSupportedError';
import { ErrorBoundaryMessage } from 'app/components/ErrorBoundary/ErrorBoundaryMessage';
import { TextEditorErrorBoundary } from 'app/components/Song/Lyrics/TextEditorErrorBoundary';
import { ContentContainer } from 'app/components/Core';

interface LyricsV2Props {
  children?: ReactNode;
  readonly?: boolean;
  emptyElement?: React.ReactNode;
}

export const LyricsV2 = ({
  children,
  emptyElement,
  readonly = false,
}: LyricsV2Props) => {
  const dispatch = useAppDispatch();
  const song = useAppSelector(getSong);
  const [connectionLink, setConnectionLink] = useState<{
    token: string;
    href: string;
  }>();
  const [config, setConfig] = useState<Lyrics2Configuration>();
  const [error, setError] = useState('');
  const [isDownloading, setIsDownloading] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const lyricSheet = useAppSelector(getLyricSheet);
  const lyricVersion = useAppSelector(getLyricVersion);

  const songAttrs = useMemo(
    () => ({
      songGuid: song?.guid || '',
      songName: song?.title || '',
    }),
    [song]
  );

  useEffect(() => {
    const initLyrics = async () => {
      setIsLoading(true);
      try {
        const response = await dispatch(
          getLyrics2ConnectionEffect({
            songGuid: song?.guid || '',
            songName: song?.title || '',
          })
        ).unwrap();
        setConnectionLink(response?.connect);
        setConfig(response?.configuration);
      } catch (e: any) {
        if (
          e?.response?.data?.error?.errorCode ===
          ApiErrorCodeTypes.CONFIGURATION_VERSION_NOT_SUPPORTED
        ) {
          setError(ApiErrorCodeTypes.CONFIGURATION_VERSION_NOT_SUPPORTED);
        } else {
          setError('unknown');
        }
      } finally {
        setIsLoading(false);
      }
    };

    if (lyricVersion === 2) {
      initLyrics();
    }
  }, [dispatch, lyricVersion, song?.guid, song?.title]);

  const handleDownloadPdf = async () => {
    setIsDownloading(true);
    await dispatch(downloadLyricsPdfEffect({ lyricGuid: lyricSheet?.guid }));
    setIsDownloading(false);
  };

  const { provider, yDoc, synced } = useProvider({
    connectionLink,
    songAttrs,
  });

  const showLoader =
    isLoading || !connectionLink || !config || !yDoc || !provider || !synced;

  if (error === ApiErrorCodeTypes.CONFIGURATION_VERSION_NOT_SUPPORTED) {
    return <NotSupportedError />;
  }

  if (error) {
    return <ErrorBoundaryMessage />;
  }

  if (showLoader) {
    return <LyricsFormLoader />;
  }

  return (
    <ContentContainer
      type={ContentContainerType.LIST}
      isViewContent
      pt={4}
      pb={6}
    >
      {children}
      <TextEditorErrorBoundary>
        <TextEditor
          provider={provider}
          yDoc={yDoc}
          config={config}
          onDownload={handleDownloadPdf}
          isDisabledDownloadButton={isDownloading}
          emptyElement={emptyElement}
          readonly={readonly}
        />
      </TextEditorErrorBoundary>
    </ContentContainer>
  );
};
